import React, { useEffect, useState } from "react";
import styled, { keyframes, createGlobalStyle } from "styled-components";
import { TypeAnimation } from "react-type-animation";
import toast, { Toaster } from "react-hot-toast";
import axios from "axios";
import { BASE_URL } from "../../api/Api";

// Global style pour activer le scroll fluide
const GlobalStyle = createGlobalStyle`
  html {
    scroll-behavior: smooth;
  }
`;

// Couleurs de base inspirées des guidelines Toyaloo
const primaryColor = "#FF45FF"; // couleur principale

// Animation de fond en dégradé
const gradientAnimation = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

// -----------------------
// Header immersif
// -----------------------
const HeaderContainer = styled.header`
  position: relative;
  height: 100vh;
  background: url('https://via.placeholder.com/1920x1080?text=New+World') no-repeat center center/cover;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: #fff;
  z-index: -2;
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(45deg, rgba(20,24,62,0.8), rgba(255,69,255,0.8));
  background-size: 200% 200%;
  animation: ${gradientAnimation} 15s ease infinite;
  z-index: 1;
`;

const HeaderContent = styled.div`
  position: relative;
  z-index: 2;
  text-align: center;
  padding: 0 20px;
`;

const HeaderTitle = styled.h1`
  font-size: 48px;
  text-transform: uppercase;
  font-weight: bold;
  margin: 0;
  font-family: "Montserrat", sans-serif;
`;

const AnimatedSubTitle = styled(TypeAnimation)`
  font-size: 24px;
  margin-top: 20px;
  font-family: "Orbitron", sans-serif;
`;

// -----------------------
// Sections de base
// -----------------------
const Section = styled.section`
  padding: 60px 20px;
  text-align: center;
  position: relative;
`;

const SectionTitle = styled.h2`
  text-align: center;
  margin-bottom: 40px;
  font: 700 50px Volkhov, sans-serif;
  @media (max-width: 991px) {
    font-size: 40px;
  }
`;

const SectionText = styled.p`
  font-size: 18px;
  line-height: 1.6;
  max-width: 800px;
  margin: 0 auto;
`;

// -----------------------
// Section À propos
// -----------------------
const AboutSection = styled(Section)`
  background: linear-gradient(45deg, #ffffff, #ffeef8);
  background-size: 200% 200%;
  animation: ${gradientAnimation} 8s ease infinite;
  border-radius: 10px;
  margin: 20px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  color: #14183e;
`;

// -----------------------
// Sections Mission / Problems / Solution / Products
// (gardées identiques)
// -----------------------
const MissionSection = styled(Section)`
  background: linear-gradient(135deg, #AF00F1, #7B1FA2);
  background-size: 200% 200%;
  animation: ${gradientAnimation} 12s ease infinite;
  border-radius: 10px;
  margin: 20px;
  color: #fff;
`;

const ProblemsSection = styled(Section)`
  background: linear-gradient(45deg, #14183e, #333);
  background-size: 200% 200%;
  animation: ${gradientAnimation} 15s ease infinite;
  border-radius: 10px;
  margin: 20px;
  color: #fff;
`;

const SolutionSection = styled(Section)`
  background: linear-gradient(135deg, #7B1FA2, #512DA8);
  background-size: 200% 200%;
  animation: ${gradientAnimation} 10s ease infinite;
  border-radius: 10px;
  margin: 20px;
  color: #fff;
`;

const ProductsSection = styled(Section)`
  background: linear-gradient(135deg, #C2185B, #7B1FA2);
  background-size: 200% 200%;
  animation: ${gradientAnimation} 12s ease infinite;
  border-radius: 10px;
  margin: 20px;
  color: #fff;
`;

// -----------------------
// Section Équipe - implémentation with hover/detail overlay
// -----------------------
const TeamSection = styled(Section)`
  background: #f9f9f9;
`;

const TeamContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 30px;
  margin-top: 40px;
`;

// Carte de base pour chaque membre
const TeamCardHover = styled.div`
  position: relative;
  width: 260px;
  height: 340px;
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  text-align: center;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  cursor: pointer;
  z-index: ${props => (props.active ? 200 : 1)};
  overflow: visible; /* allow detail card to overflow */
  &:hover {
    transform: scale(1.04);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.18);
  }
   /* 📱 responsive */
  @media (max-width: 600px) {
    width: 90%;
    height: auto;
  }
`;

const TeamImage = styled.img`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  border: 3px solid ${primaryColor};
  margin-bottom: 15px;
  object-fit: cover;
  object-position: top;
  image-rendering: crisp-edges;
  transition: transform 0.3s;
  &:hover {
    transform: rotate(6deg) scale(1.02);
  }
`;

const TeamName = styled.h3`
  font-size: 22px;
  margin-bottom: 5px;
  font-weight: bold;
  color: #14183e;
`;

const TeamRole = styled.p`
  font-size: 16px;
  color: #555;
`;

// Carte de détail qui apparaît au survol (overlay flottant)
const TeamDetailCard = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) scale(${props => (props.show ? 1 : 0.86)});
  width: 320px;
  height: 420px;
  background: url(${props => props.bgImage}) center/cover no-repeat;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 0 24px rgba(255, 69, 255, 0.18), 0 0 48px rgba(181, 17, 117, 0.12);
  opacity: ${props => (props.show ? 1 : 0)};
  visibility: ${props => (props.show ? 'visible' : 'hidden')};
  transition: all 0.28s ease-in-out;
  z-index: 300;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  overflow: hidden;
  pointer-events: auto; /* avoid accidental clicks on the overlay itself */

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0,0,0,0.55);
    border-radius: 12px;
    z-index: 0;
  }

  /* 📱 responsive */
  @media (max-width: 600px) {
    width: 90%;
    height: auto;
    min-height: 300px; /* pour garder un peu de hauteur */
  }
`;

const TeamDetailContent = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  box-sizing: border-box;
  text-align: center;
  pointer-events: auto; /* rendre les liens cliquables */
`;

const SocialLinks = styled.div`
  margin-top: 15px;
  display: flex;
  gap: 15px;

  a {
    transition: transform 0.3s ease, color 0.3s ease;
    color: white; /* couleur par défaut */
  }

  a:hover {
    transform: scale(1.2);
    color: #ffd700;
  }

  /* 📱 agrandir un peu les icônes en mobile */
  @media (max-width: 600px) {
    svg {
      width: 28px;
      height: 28px;
    }
  }
`;

const LinkedInIcon = () => (
  <svg width="22" height="22" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.761 0 5-2.239 5-5v-14c0-2.761-2.239-5-5-5zm-11 19h-3v-10h3v10zm-1.5-11.268c-.966 0-1.75-.784-1.75-1.75s.784-1.75 1.75-1.75 1.75.784 1.75 1.75-.784 1.75-1.75 1.75zm13.5 11.268h-3v-5.604c0-1.337-.027-3.063-1.867-3.063-1.868 0-2.154 1.459-2.154 2.967v5.7h-3v-10h2.881v1.367h.041c.401-.761 1.379-1.563 2.838-1.563 3.036 0 3.6 2.001 3.6 4.601v5.595z" /></svg>
);
const TwitterIcon = () => (
  <svg width="22" height="22" fill="currentColor" viewBox="0 0 24 24"><path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-2.719 0-4.924 2.205-4.924 4.924 0 .386.044.762.127 1.124-4.09-.205-7.719-2.165-10.148-5.144-.424.729-.666 1.577-.666 2.483 0 1.713.872 3.229 2.197 4.117-.809-.026-1.57-.248-2.237-.616v.062c0 2.393 1.703 4.393 3.965 4.844-.415.113-.853.174-1.304.174-.319 0-.627-.031-.929-.089.627 1.956 2.444 3.377 4.6 3.417-1.685 1.32-3.808 2.107-6.115 2.107-.398 0-.79-.023-1.175-.069 2.179 1.397 4.768 2.215 7.557 2.215 9.054 0 14.009-7.504 14.009-14.009 0-.213-.005-.426-.014-.637.962-.695 1.797-1.562 2.457-2.549z" /></svg>
);

// -----------------------
// Section Contact (classique)
// -----------------------
const ContactSection = styled(Section)`
  background: #fff;
  border-radius: 10px;
  margin: 20px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  text-align: left;
`;

const ContactForm = styled.form`
  max-width: 600px;
  margin: 0 auto;
  padding: 20px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 16px;
  margin-bottom: 8px;
  font-weight: bold;
  color: #14183e;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  font-size: 16px;
  border: 2px solid #ddd;
  border-radius: 5px;
  transition: border 0.3s;
  &:focus {
    border-color: ${primaryColor};
    outline: none;
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 12px;
  font-size: 16px;
  border: 2px solid #ddd;
  border-radius: 5px;
  transition: border 0.3s;
  resize: vertical;
  min-height: 120px;
  &:focus {
    border-color: ${primaryColor};
    outline: none;
  }
`;

const SubmitButton = styled.button`
  padding: 10px 20px;
  font-size: 16px;
  color: white;
  background: #ff4592;
  border: none;
  border-radius: 5px;
  cursor: pointer;

  &:hover {
    background-color: #b61175;
    transform: scale(1.05);
  }
`;

// -----------------------
// Composant App
// -----------------------
const Info = () => {
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const form = e.target;
    const data = {
      name: form.name.value,
      email: form.email.value,
      subject: form.subject.value,
      message: form.message.value,
    };

    try {
      const res = await axios.post(`${BASE_URL}process/mail/send`, data);
      if (res.data.success) {
        toast.success(res.data.message);
        form.reset();
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      toast.error("Une erreur s'est produite lors de l'envoi du message.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Informations sur chaque membre de l'équipe
  const teamMembers = [
    {
      name: "Emmanuel EBEYA",
      role: "CEO",
      img: "/Assets/067A0162.jpg",
      bio: "Entrepreneur passionné par l'éducation et la technologie.",
      socials: {
        linkedin: "https://www.linkedin.com/in/emmanuel-ebeya-271182148?utm_source=share&utm_campaign=share_via&utm_content=profile&utm_medium=ios_app",
        // twitter: "https://twitter.com/emmanuelmilito",
      },
    },
    {
      name: "Veronica KAKIESSE",
      role: "Responsable Design",
      img: "/Assets/vero.jpeg",
      bio: "Designer créative, experte en UX/UI.",
      socials: {
        linkedin: "https://www.linkedin.com/in/veronica-k-029881203?utm_source=share&utm_campaign=share_via&utm_content=profile&utm_medium=ios_app",
      },
    },
    {
      name: "Lazare MBURUSAKC",
      role: "Responsable Recherche",
      img: "/Assets/067A0212.jpg",
      bio: "Chercheur en pédagogie innovante.",
      socials: {
        linkedin: "https://www.linkedin.com/in/mburusakc?utm_source=share&utm_campaign=share_via&utm_content=profile&utm_medium=ios_app",
      },
    },
    {
      name: "Rinedi NDJALI",
      role: "Responsable Système",
      img: "/Assets/067A0233.jpg",
      bio: "Ingénieur système et sécurité informatique.",
      socials: {
        linkedin: "https://www.linkedin.com/in/rinedi-ndjali-41679626b?utm_source=share&utm_campaign=share_via&utm_content=profile&utm_medium=ios_app",
      },
    },
    {
      name: "Joel LUKAU",
      role: "Chargé de relation publique",
      img: "/Assets/067A0200.jpg",
      bio: "Expert en communication et relations publiques.",
      socials: {
        linkedin: "https://www.linkedin.com/in/jo%C3%ABl-lukau-64809a22b?utm_source=share&utm_campaign=share_via&utm_content=profile&utm_medium=ios_app",
      },
    },
    {
      name: "Annael NZINGA",
      role: "Responsable pédagogique",
      img: "/Assets/067A0256.jpg",
      bio: "Spécialiste en pédagogie et formation.",
      socials: {
        linkedin: "https://www.linkedin.com/in/anna%C3%ABl-nzinga-736742235?utm_source=share&utm_campaign=share_via&utm_content=profile&utm_medium=ios_app",
      },
    },
    {
      name: "Christian KANYIMBO",
      role: "Chargé de finance",
      img: "/Assets/067A0325.jpg",
      bio: "Gestionnaire financier et analyste.",
      socials: {
        linkedin: "",
      },
    },
  ];

  // --- état pour gérer quel membre est en hover / focus ---
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [hoverTimeout, setHoverTimeout] = useState(null);

  const handleMemberMouseEnter = (idx) => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
    setHoveredIndex(idx);
  };

  const handleMemberMouseLeave = () => {
    // petit délai pour éviter le clignotement
    const t = setTimeout(() => setHoveredIndex(null), 250);
    setHoverTimeout(t);
  };

  const handleMemberClick = (idx) => {
    if (hoveredIndex === idx) {
      setHoveredIndex(null); // refermer si déjà ouvert
    } else {
      setHoveredIndex(idx); // ouvrir
    }
  };


  return (
    <>
      <GlobalStyle />
      <HeaderContainer>
        <Overlay />
        <HeaderContent>
          <HeaderTitle>Toyaloo</HeaderTitle>
          <AnimatedSubTitle
            sequence={[
              "Apprendre en jouant", 2000,
              "Un futur accessible à tous", 2000,
            ]}
            wrapper="div"
            speed={50}
            repeat={Infinity}
          />
        </HeaderContent>
      </HeaderContainer>

      <AboutSection id="about">
        <SectionTitle>À propos</SectionTitle>
        <SectionText>
          Toyaloo est une plateforme web innovante située à Kinshasa qui révolutionne l'éducation en rendant l'apprentissage accessible à tous grâce à des méthodes ludiques.
        </SectionText>
      </AboutSection>

      <MissionSection id="mission">
        <SectionTitle>Mission</SectionTitle>
        <SectionText>
          La mission de Toyaloo est de rendre l'éducation accessible à tous, en particulier aux plus démunis, par le biais de l'apprentissage ludique. Nous croyons que le plaisir de jouer contribue à un apprentissage durable et efficace, et nous nous engageons à offrir des ressources éducatives qui encouragent la curiosité, la créativité et l'épanouissement personnel.
        </SectionText>
      </MissionSection>

      <ProblemsSection id="problems">
        <SectionTitle>Problèmes</SectionTitle>
        <SectionText>
          Dans de nombreuses régions, y compris Kinshasa, l'accès à une éducation de qualité reste limité, en particulier pour les enfants et les adultes issus de milieux défavorisés. Les méthodes d'enseignement traditionnelles peuvent souvent sembler ennuyeuses et peu engageantes, ce qui crée un fossé d'inefficacité dans l'apprentissage. Ce manque d'accessibilité à des ressources éducatives innovantes entrave le développement des compétences essentielles.
        </SectionText>
      </ProblemsSection>

      <SolutionSection id="solution">
        <SectionTitle>Solution</SectionTitle>
        <SectionText>
          Toyaloo répond à ce défi en proposant une plateforme d'apprentissage par le jeu qui transforme l'éducation en une expérience interactive et ludique. Grâce à des jeux éducatifs, des vidéos interactives et d'autres activités engageantes, nous faisons de l'apprentissage une aventure agréable. En intégrant des éléments de jeu motivants, Toyaloo favorise l'acquisition de connaissances tout en s'amusant.
        </SectionText>
      </SolutionSection>

      <ProductsSection id="products">
        <SectionTitle>Produits & Services</SectionTitle>
        <SectionText>
          Les principaux produits et services offerts par Toyaloo incluent une large gamme de jeux éducatifs en ligne, des ressources pédagogiques diversifiées et des modules de formation ciblant des compétences spécifiques. De plus, Toyaloo propose des ateliers et des événements communautaires pour sensibiliser et engager les familles et les éducateurs sur l'importance de l'apprentissage ludique.
        </SectionText>
      </ProductsSection>

      <TeamSection id="team">
        <SectionTitle>Équipe</SectionTitle>
        <TeamContainer>
          {teamMembers.map((member, idx) => (
            <TeamCardHover
              key={member.name}
              active={hoveredIndex === idx}
              tabIndex={0}
              onMouseEnter={() => handleMemberMouseEnter(idx)}
              onMouseLeave={handleMemberMouseLeave}
              onFocus={() => handleMemberMouseEnter(idx)}
              onBlur={() => handleMemberMouseLeave()}
              onClick={() => handleMemberClick(idx)} // ajout pour mobile
              aria-expanded={hoveredIndex === idx}
            >
              <TeamImage src={member.img} alt={member.name} />
              <TeamName>{member.name}</TeamName>
              <TeamRole>{member.role}</TeamRole>

              <TeamDetailCard show={hoveredIndex === idx} bgImage={member.img}>
                <TeamDetailContent>
                  <TeamName style={{ color: "#fff" }}>{member.name}</TeamName>
                  <TeamRole style={{ color: "#fff" }}>{member.role}</TeamRole>
                  <SectionText style={{ color: "#fff", fontSize: "16px", marginTop: "10px" }}>
                    {member.bio}
                  </SectionText>
                  <SocialLinks>
                    {member.socials.linkedin && (
                      <a href={member.socials.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                        <LinkedInIcon />
                      </a>
                    )}
                    {member.socials.twitter && (
                      <a href={member.socials.twitter} target="_blank" rel="noopener noreferrer" aria-label="Twitter">
                        <TwitterIcon />
                      </a>
                    )}
                  </SocialLinks>
                </TeamDetailContent>
              </TeamDetailCard>
            </TeamCardHover>
          ))}
        </TeamContainer>
      </TeamSection>

      <ContactSection id="contact">
        <SectionTitle>Contact</SectionTitle>
        <ContactForm onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="name">Nom</Label>
            <Input type="text" name="name" id="name" placeholder="Votre nom" />
          </FormGroup>
          <FormGroup>
            <Label htmlFor="email">Email</Label>
            <Input type="email" name="email" id="email" placeholder="Votre email" />
          </FormGroup>
          <FormGroup>
            <Label htmlFor="subject">Sujet</Label>
            <Input type="text" name="subject" id="subject" placeholder="Votre sujet" />
          </FormGroup>
          <FormGroup>
            <Label htmlFor="message">Message</Label>
            <Textarea id="message" name="message" placeholder="Votre message" />
          </FormGroup>
          {loading ? (
            <SubmitButton disabled>Envoi en cours...</SubmitButton>
          ) : (
            <SubmitButton $primary type="submit">Envoyer le message</SubmitButton>
          )}
        </ContactForm>
      </ContactSection>
      <Toaster position="top-center" reverseOrder={false} />
    </>
  );
};

export default Info;
